import React, { useState } from 'react'
import styled from 'styled-components'
import Select from 'react-select'
import Modal from 'components/_UI/Modal'
import Button from 'components/_UI/Button'
import Loader from 'components/_UI/Loader'
import Text from 'components/_UI/Text'
import Fieldset from 'components/_UI/Fieldset'
import Radio from 'components/_UI/Radio'
import { useEvent } from 'context/events/Event'
import sponsorSectionsApi from 'api/requests/sponsorSections'
import colors from 'theme/colors'
import breakpoints from 'theme/breakpoints'
import { Column } from 'styled/alignment/Column'
import { Row } from 'styled/alignment/Row'

export const typeOptions = [
    { label: 'Parceiro(s)', value: 'partner' },
    { label: 'Apoiador(es)', value: 'supporter' },
    { label: 'Patrocinador(es)', value: 'sponsor' },
    { label: 'Mantenedor(es)', value: 'maintainer' },
    { label: 'Outro', value: 'other' },
]

export const sizeOptions = [
    { label: 'Muito pequeno', value: 'xs' },
    { label: 'Pequeno', value: 'sm' },
    { label: 'Médio', value: 'md' },
    { label: 'Grande', value: 'lg' },
]

// Nome no singular de cada item, usado para compor textos como "Adicionar apoiador".
export const itemSingularLabels = {
    partner: 'parceiro',
    supporter: 'apoiador',
    sponsor: 'patrocinador',
    maintainer: 'mantenedor',
}

export function getItemLabel(type) {
    return itemSingularLabels[type] || 'item'
}

function SponsorSectionForm({ modalSection, setModalSection, onSuccess, edit = false }) {
    const { event } = useEvent()
    const [loading, setLoading] = useState(false)
    const [selectedType, setSelectedType] = useState(
        edit ? typeOptions.find(option => option.value === edit.type) : null
    )
    const [customTitle, setCustomTitle] = useState(edit?.custom_title || '')
    const [selectedSize, setSelectedSize] = useState(edit?.logo_size || 'md')
    const [typeError, setTypeError] = useState(false)
    const [titleError, setTitleError] = useState(false)

    function createPayload() {
        return {
            type: selectedType.value,
            custom_title: selectedType.value === 'other' ? customTitle : null,
            logo_size: selectedSize,
        }
    }

    async function handleSubmit(e) {
        e.preventDefault()

        if (!selectedType) {
            setTypeError(true)
            return
        }
        if (selectedType.value === 'other' && !customTitle.trim()) {
            setTitleError(true)
            return
        }

        setLoading(true)
        const payload = createPayload()

        try {
            if (edit) {
                await sponsorSectionsApi.update(event.slug, edit.id, payload)
                onSuccess('editSection')
            } else {
                await sponsorSectionsApi.create(event.slug, payload)
                onSuccess('createSection')
            }
        } catch (error) {
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal isOpen={!!modalSection} onClose={() => setModalSection(false)}>
            <Text name='highlight'>{!edit ? 'Adicionar' : 'Atualizar'} seção</Text>
            <Form onSubmit={handleSubmit}>
                <Text paddingBottom={'5px'}>Tipo de seção *</Text>
                <Select
                    placeholder='Selecione o tipo'
                    value={selectedType}
                    options={typeOptions}
                    onChange={(option) => {
                        setSelectedType(option)
                        setTypeError(false)
                    }}
                />
                {typeError && (
                    <Column>
                        <Text color={colors.danger} name='small'>Selecione o tipo da seção.</Text>
                    </Column>
                )}

                {selectedType?.value === 'other' && (
                    <FieldWrap>
                        <Fieldset
                            label='Nome da seção *'
                            placeholder='Ex: Colaboradores'
                            layout='squired'
                            value={customTitle}
                            onChangeValue={(value) => {
                                setCustomTitle(value)
                                setTitleError(false)
                            }}
                        />
                        {titleError && (
                            <Column>
                                <Text color={colors.danger} name='small'>Informe o nome da seção.</Text>
                            </Column>
                        )}
                    </FieldWrap>
                )}

                <FieldWrap>
                    <Text paddingBottom={'5px'}>Tamanho dos logos *</Text>
                    <SizeOptions>
                        {sizeOptions.map((option) => (
                            <Radio
                                key={option.value}
                                selected={selectedSize === option.value}
                                label={option.label}
                                onSelect={() => setSelectedSize(option.value)}
                            />
                        ))}
                    </SizeOptions>
                </FieldWrap>

                {loading ? (
                    <Loader />
                ) : (
                    <ContentButton>
                        <Button
                            variant={'light'}
                            category={'roundedBorded'}
                            onClick={() => setModalSection(false)}
                        >
                            Cancelar
                        </Button>
                        <Button variant='featured' type={'submit'}>
                            {!edit ? 'Adicionar' : 'Atualizar'}
                        </Button>
                    </ContentButton>
                )}
            </Form>
        </Modal>
    )
}

export default SponsorSectionForm

const Form = styled.form`
    width: 100%;
`;

const FieldWrap = styled(Column)`
    width: 100%;
    align-items: flex-start;
    margin-top: 15px;
`;

const SizeOptions = styled(Row)`
    flex-wrap: wrap;
    gap: 0 20px;
`;

const ContentButton = styled(Column)`
    margin-top: 15px;
    @media screen and (min-width: ${breakpoints.md}){
        flex-direction: row;
        gap: 5px;
    }
`;
